import { Position } from "@xyflow/react";
import type { CSSProperties } from "react";

export type LayoutDirection = "LR" | "TB";

/**
 * Resolve React Flow handle positions from layout direction.
 * LR → left/right handles; TB → top/bottom handles.
 */
export function getHandlePositions(layoutDirection: LayoutDirection = "LR") {
  const isHorizontal = layoutDirection === "LR";
  return {
    isHorizontal,
    inputPosition: isHorizontal ? Position.Left : Position.Top,
    outputPosition: isHorizontal ? Position.Right : Position.Bottom,
  };
}

/**
 * Offset style for dual handles on the same edge
 * (e.g. left/right operands).
 *
 * Dagre stacks the first successor opposite to top→bottom / left→right
 * handle order (same compensation as workflow FanOutNode). So the first
 * operand ("left") is placed at the far offset and the second ("right")
 * at the near offset to keep edges uncrossed.
 */
export function getDualHandleOffsetStyle(
  isHorizontal: boolean,
  which: "first" | "second"
): CSSProperties {
  // first → 70%, second → 30% (dagre reverses sibling order vs handle axis)
  const offsetPercent = which === "first" ? "70%" : "30%";
  return handleOffsetStyle(isHorizontal, offsetPercent);
}

/** Raw percent offset along the handle axis (top for LR, left for TB). */
export function handleOffsetStyle(
  isHorizontal: boolean,
  offsetPercent: string
): CSSProperties {
  return isHorizontal ? { top: offsetPercent } : { left: offsetPercent };
}
